import React from 'react';
import { ASSETS } from '../../assetManifest.js';
import { CONSTANTS } from '../../data.js';
import { groupDragonsByRarity, getRarityColor, getElementColor, getElementGlow } from '../../utils/gameUtils.js';
import { getAssetKey } from '../../utils/assetUtils.js';

const IndexTab = ({ indexUnlockedDragons }) => {
  const indexDragons = groupDragonsByRarity();
  const totalDragons = Object.keys(CONSTANTS.DRAGONS).length;
  const unlockedCount = indexUnlockedDragons.length;
  const progressPercentage = totalDragons > 0 ? (unlockedCount / totalDragons) * 100 : 0;

  return (
    <div className="card-enhanced h-full flex flex-col">
      <div className="flex items-center space-x-3 mb-4 flex-shrink-0">
        <h2 className="text-2xl font-mono font-semibold text-yellow-400 text-glow">Dragon Index</h2>
      </div>
      
      <div className="mb-6 bg-black/20 p-4 rounded-lg border border-white/10">
        <div className="flex justify-between items-center mb-2 font-mono">
          <span className="text-gray-300">Total Dragons Collected</span>
          <span className="text-yellow-300 font-bold">{unlockedCount} / {totalDragons}</span>
        </div>
        <div className="w-full bg-gray-700/50 rounded-full h-4 border border-black/50">
          <div
            className="bg-gradient-to-r from-yellow-500 to-yellow-300 h-full rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>
        <div className="text-right text-xs font-sans text-yellow-400 mt-1">{progressPercentage.toFixed(1)}% Complete</div>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 -mr-2">
        {Object.entries(indexDragons).map(([rarity, dragons]) => (
          <div key={rarity} className="mb-6">
            <h3 className="text-lg font-mono font-bold mb-3 flex items-center" style={{ color: getRarityColor(rarity) }}>
              {rarity} Dragons
              <div className="ml-2 w-3 h-3 rounded-full" style={{ backgroundColor: getRarityColor(rarity) }}></div>
              <span className="ml-3 text-sm text-gray-400 font-sans">
                ({dragons.filter(d => indexUnlockedDragons.includes(d.name)).length}/{dragons.length})
              </span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {dragons.map(dragon => {
                const isUnlocked = indexUnlockedDragons.includes(dragon.name);

                return (
                  <div key={dragon.name} className={`bg-gray-900/50 p-4 rounded-lg border border-transparent relative overflow-hidden transition-all duration-300 ${
                    !isUnlocked ? 'opacity-60' : ''
                  }`}
                      style={{ '--element-color': getElementColor(dragon.element) }}>
                    <div className="absolute top-0 left-0 w-full h-1 bg-[var(--element-color)] opacity-50"></div>
                    <div className="flex flex-col items-center text-center">
                      <div className="relative mb-3">
                        <img
                          src={ASSETS.dragons[getAssetKey(dragon.name)].path}
                          alt={dragon.name}
                          className="w-16 h-16"
                          style={{ 
                            filter: isUnlocked 
                              ? `drop-shadow(0 0 6px ${getElementGlow(dragon.element)})` 
                              : 'grayscale(100%) brightness(0.5)'
                          }}
                        />
                        {!isUnlocked && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="text-3xl text-gray-800">?</div>
                          </div>
                        )}
                      </div>
                      <p className="text-sm font-mono font-bold mb-1">
                        {isUnlocked ? dragon.name : '???'}
                      </p>
                      <p className="text-xs font-sans mb-1" style={{ color: getRarityColor(dragon.rarity) }}>
                        {dragon.rarity}
                      </p>
                    </div>

                    {isUnlocked ? (
                      <div className="bg-black/20 p-2 rounded space-y-1 mt-2 font-sans">
                        <div className="text-xs text-gray-300 italic text-center mb-2">
                          "{CONSTANTS.DRAGON_DESCRIPTIONS[dragon.name] || 'A mysterious dragon'}"
                        </div>
                        <div className="grid grid-cols-3 gap-x-1 text-xs pt-2 mt-2 border-t border-white/10 text-gray-400">
                            <span title="Attack">ATK: {dragon.stats.attack}</span>
                            <span title="Speed">SPD: {dragon.stats.speed}</span>
                            <span title="Defense">DEF: {dragon.stats.defense}</span>
                            <span title="Strength">STR: {dragon.stats.strength}</span>
                            <span title="Physique">PHY: {dragon.stats.physique}</span>
                            <span title="Hidden Power">HPW: {dragon.stats.hiddenPower}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-black/20 p-2 rounded mt-2">
                        <p className="text-xs text-gray-400 text-center font-sans">Discover to unlock</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default IndexTab;
